import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { finalize, take, tap } from 'rxjs';
import { TaxDocDetail } from 'src/app/models/tax-doc-detail';
import { TaxDocSummary } from 'src/app/models/tax-doc-summary';
import { TaxDocClient } from 'src/app/TaxDocNotifier-api';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-details',
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss'
})
export class DetailsComponent implements OnInit {  
  loadId: number;

  summaryLoading: boolean = true;
  detailsLoading: boolean = true;
  error: boolean = false;

  sendLoading: boolean = false;
  sendSuccess: boolean = false;
  sendFailure: boolean = false;
  sendText: string = '';

  searchNotFound: boolean = false;
  pageSearchLoading: boolean = true;
  acctSearchError: boolean = false;
  searchDisplayed: boolean = false;

  taxDocSummary: TaxDocSummary = null;
  taxDocDetails: TaxDocDetail[] = null;
  currentPageDetails: TaxDocDetail[] = null;

  searchForm: FormGroup;

  currentPage: number = 1;
  pageCount: number;

  constructor(private readonly route: ActivatedRoute,
    private taxDocClientService: TaxDocClient,
    private readonly fb: UntypedFormBuilder) {
    this.searchForm = this.fb.group({
      search: [
        '',
        [Validators.pattern(/^\d{8}$|^\d{9}$|^\d{12}$/)]
      ]
    });
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        take(1),
        tap(x => this.loadId = Number(x.get('loadId')))
      )
      .subscribe(_ => {
        this.getTaxDocSummary();
        this.getTaxDocDetails(1);
        this.getPageCount();
      });
  }

  getTaxDocSummary(): void {
    this.taxDocClientService.getTaxDocSummary(this.loadId)
      .subscribe({
        next: res => {
          this.taxDocSummary = res;
          this.summaryLoading = false;
        },
        error: () => {
          this.error = true;
          this.summaryLoading = false;
        }
      });
  }

  getTaxDocDetails(page: number): void {
    this.pageSearchLoading = true;
    this.taxDocClientService.getTaxDocDetails(this.loadId, page, environment.detailsPageSize)
      .pipe(finalize(() => {
        this.detailsLoading = false;
        this.pageSearchLoading = false;
      }))
      .subscribe({
        next: res => {
          this.taxDocDetails = res;
          this.currentPageDetails = this.taxDocDetails;
        },
        error: () => {
          this.error = true;
        }
      });
  }

  sendAllEmails(): void {
    this.sendLoading = true;

    this.taxDocClientService.approveTaxLoad(this.loadId)
      .pipe(finalize(() => {
        this.sendLoading = false;
      }))
      .subscribe({
        next: res => {
          this.sendSuccess = res;
          this.sendFailure = !res;

          if (res)
          {
            this.sendText = 'Emails have begun queueing. Please monitor dbBatchEmail_IT to check progress.';
            this.taxDocSummary.sent = true;
          }
          else
          {
            this.sendText = 'You are not authorized to perform this action. Please contact your Tech Lead for assistance.';
          }
        },
        error: () => {
          this.sendFailure = true;
          this.sendSuccess = false;
          this.sendText = 'An error occured queueing emails. Please try again later.';
        }
      });
  }

  getFirstPage(): void {
    this.currentPage = 1;
    this.getTaxDocDetails(this.currentPage);
  }
  getPreviousPage(): void {
    this.getTaxDocDetails(--this.currentPage);
  }

  getNextPage(): void {
    this.getTaxDocDetails(++this.currentPage);
  }

  getLastPage(): void{
    this.currentPage = this.pageCount;
    this.getTaxDocDetails(this.currentPage);
  }

  getPageCount(): void {
    this.taxDocClientService.getPageCountForLoad(this.loadId, environment.detailsPageSize)
      .subscribe({
        next: res => {
          this.pageCount = res;
        },
        error: () => {
          this.error = true;
        }
      });
  }

  triggerDismiss(): void {
    this.sendSuccess = false;
    this.sendFailure = false;
  }

  isValidInput(): boolean {
    return this.taxDocSummary.sent || this.searchForm.valid;
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.taxDocDetails = this.currentPageDetails;
    this.searchNotFound = false;
    this.acctSearchError = false;
    this.searchDisplayed = false;
  }

  search(): void {
    if (this.isValidInput()) {
      this.pageSearchLoading = true;
      this.searchNotFound = false;
      this.acctSearchError = false;

      const searchTerm = this.searchForm.get('search')?.value;
      if (searchTerm == '') {
        this.taxDocDetails = this.currentPageDetails;
        this.pageSearchLoading = false;
        this.searchDisplayed = false;
      }
      else {
        this.taxDocClientService.getTaxDocDetailsBySearchTerm(this.loadId, searchTerm, this.taxDocSummary.sent)
          .pipe(finalize(() => {
            this.pageSearchLoading = false;
            this.searchDisplayed = true;
          })).subscribe({
            next: res => {
              if (res?.length == 0) {
                this.searchNotFound = true;
              }

              this.taxDocDetails = res;
            },
            error: () => {
              this.acctSearchError = true;
            }
          });
      }
    }
  }
}
